import type {
  ColumnDef,
  OnChangeFn,
  PaginationState,
} from "@tanstack/react-table";
import type { ReactNode } from "react";
import { SimpleTable } from "@/core/components/simple-table";
import { Card, CardContent } from "@volca/ui/components/ui/card";
import { TOOL_PEGBOARD } from "@volca/ui/components/ui/empty-machine";
import { MachineEmptyState } from "@volca/ui/components/ui/machine-empty-state";
import { Stack } from "@volca/ui/components/ui/stack";
import { useIsMobile } from "@volca/ui/hooks/use-mobile";
import { ROSTER_NAME_COLUMN_ID } from "@/payroll/roster/cols";
import { RosterFilterEmptyState } from "@/payroll/roster/roster-filter-empty-state";
import type {
  PayrollRosterDraftMember as DraftMember,
  PayrollRosterMember,
} from "@/payroll/roster/types";

interface RosterTableCardProps {
  controls: ReactNode;
  displayedCount: number;
  filteredDrafts: DraftMember[];
  columns: ColumnDef<DraftMember>[];
  tableMinWidth: number;
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  flaggedMembers: Set<PayrollRosterMember>;
  departedIds: Set<string>;
  onRowClick: ((draft: DraftMember) => void) | undefined;
  scrollResetKey: string;
  search: string;
  activeFilterCount: number;
  onClearFilters: () => void;
}

export const RosterTableCard = ({
  controls,
  displayedCount,
  filteredDrafts,
  columns,
  tableMinWidth,
  pagination,
  onPaginationChange,
  flaggedMembers,
  departedIds,
  onRowClick,
  scrollResetKey,
  search,
  activeFilterCount,
  onClearFilters,
}: RosterTableCardProps) => {
  const isMobile = useIsMobile();
  return (
    <Card className="flex min-h-0 w-full min-w-0 flex-1 flex-col gap-0 overflow-hidden py-0">
      <Stack align="start" className="w-full shrink-0 border-b px-4 py-3">
        {controls}
      </Stack>
      <CardContent className="flex min-h-0 flex-1 flex-col p-0">
        {displayedCount === 0 ? (
          <MachineEmptyState
            art={TOOL_PEGBOARD}
            title="No roster members yet"
            description="Add the first person to start this payroll roster."
          />
        ) : filteredDrafts.length === 0 ? (
          <RosterFilterEmptyState
            search={search}
            activeFilterCount={activeFilterCount}
            onClear={onClearFilters}
          />
        ) : (
          <Stack align="start" gap={0} className="min-h-0 w-full flex-1">
            <SimpleTable
              label="Roster"
              data={filteredDrafts}
              columns={columns}
              getRowId={(draft) => draft.id}
              enablePagination
              paginationState={pagination}
              onPaginationStateChange={onPaginationChange}
              getRowVariant={(draft) =>
                draft.suggestion
                  ? "destructive"
                  : flaggedMembers.has(draft.member) ||
                      departedIds.has(draft.member.service_titan_employee_id)
                    ? "warning"
                    : undefined
              }
              onRowClick={onRowClick}
              scrollResetKey={scrollResetKey}
              className="h-full w-full"
              scrollContentStyle={{ minWidth: tableMinWidth }}
              columnPinning={{
                left: isMobile ? [] : [ROSTER_NAME_COLUMN_ID],
              }}
              respectColumnWidths
            />
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
